import { supabase } from "@/lib/supabase";

export const FLUXO_MEDIA_BUCKET = "fluxo-media";
/** Fallback legado: áudios em data URL ainda sobem aqui no momento do envio. */
const LEGACY_AUDIO_BUCKET = "chat-audios";

const AUDIO_EXTENSION_BY_MIME: Record<string, string> = {
  "audio/mpeg": "mp3",
  "audio/mp3": "mp3",
  "audio/ogg": "ogg",
  "audio/webm": "webm",
  "audio/mp4": "m4a",
  "audio/wav": "wav",
};

const IMAGE_EXTENSION_BY_MIME: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
};

export type FluxoMediaKind = "imagem" | "audio";

export const isDataUrl = (value: string): boolean => value.startsWith("data:");

export const isHttpUrl = (value: string): boolean =>
  value.startsWith("http://") || value.startsWith("https://");

export const dataUrlToBlob = (dataUrl: string): Blob => {
  const [header, data] = dataUrl.split(",");
  if (!data) throw new Error("Arquivo inválido");

  const mimeType = header.match(/^data:([^;]+)/)?.[1] ?? "application/octet-stream";
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: mimeType });
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("Falha ao ler arquivo"));
    reader.readAsDataURL(blob);
  });

const resolveExtension = (
  kind: FluxoMediaKind,
  mimeType: string,
  fileName?: string
): string => {
  const map = kind === "imagem" ? IMAGE_EXTENSION_BY_MIME : AUDIO_EXTENSION_BY_MIME;
  const fromMime = map[mimeType.toLowerCase()];
  if (fromMime) return fromMime;

  const fromName = fileName?.split(".").pop()?.toLowerCase();
  if (fromName && fromName.length <= 4) return fromName;

  return kind === "imagem" ? "jpg" : "mp3";
};

/** Extrai o path relativo do bucket a partir de uma URL pública do Storage. */
export const extractFluxoMediaPath = (url: string): string | null => {
  if (!url) return null;
  const marker = `/object/public/${FLUXO_MEDIA_BUCKET}/`;
  const idx = url.indexOf(marker);
  if (idx < 0) return null;
  return decodeURIComponent(url.slice(idx + marker.length).split("?")[0]);
};

export const buildFluxoMediaPath = (
  accountId: string,
  fluxoId: string,
  blockId: string,
  extension: string
): string => `${accountId}/${fluxoId}/${blockId}.${extension}`;

/**
 * Sobe imagem ou áudio do editor de fluxos para o bucket fluxo-media.
 * Retorna a URL pública a ser guardada no bloco.
 */
export const uploadFluxoMedia = async (params: {
  accountId: string;
  fluxoId: string;
  blockId: string;
  file: File | Blob;
  kind: FluxoMediaKind;
  fileName?: string;
}): Promise<string> => {
  if (!supabase) throw new Error("Supabase não configurado");

  const mimeType = params.file.type || (params.kind === "imagem" ? "image/jpeg" : "audio/mpeg");
  const extension = resolveExtension(params.kind, mimeType, params.fileName);
  const filePath = buildFluxoMediaPath(
    params.accountId,
    params.fluxoId,
    params.blockId,
    extension
  );

  const { error: uploadError } = await supabase.storage
    .from(FLUXO_MEDIA_BUCKET)
    .upload(filePath, params.file, {
      contentType: mimeType,
      cacheControl: "3600",
      upsert: true,
    });

  if (uploadError) {
    throw new Error(`Não foi possível enviar o arquivo: ${uploadError.message}`);
  }

  const { data } = supabase.storage.from(FLUXO_MEDIA_BUCKET).getPublicUrl(filePath);
  if (!data?.publicUrl) {
    throw new Error("Não foi possível gerar o link público do arquivo");
  }

  return data.publicUrl;
};

/** Remove um arquivo do fluxo-media a partir da URL pública (best-effort). */
export const deleteFluxoMediaByUrl = async (url: string | null | undefined): Promise<void> => {
  if (!url || !supabase) return;
  const path = extractFluxoMediaPath(url);
  if (!path) return;

  const { error } = await supabase.storage.from(FLUXO_MEDIA_BUCKET).remove([path]);
  if (error) {
    console.warn("⚠️ [fluxoMedia] Falha ao remover arquivo:", error.message);
  }
};

/** Lista e remove todos os arquivos de um fluxo (pasta accountId/fluxoId). */
export const deleteFluxoMediaFolder = async (
  accountId: string,
  fluxoId: string
): Promise<void> => {
  if (!supabase) return;

  const prefix = `${accountId}/${fluxoId}`;
  const { data: files, error: listError } = await supabase.storage
    .from(FLUXO_MEDIA_BUCKET)
    .list(prefix);

  if (listError) {
    console.warn("⚠️ [fluxoMedia] Falha ao listar pasta do fluxo:", listError.message);
    return;
  }

  if (!files || files.length === 0) return;

  const paths = files.map((f) => `${prefix}/${f.name}`);
  const { error } = await supabase.storage.from(FLUXO_MEDIA_BUCKET).remove(paths);
  if (error) {
    console.warn("⚠️ [fluxoMedia] Falha ao limpar pasta do fluxo:", error.message);
  }
};

/**
 * send-image espera data URL/base64. Se o bloco já tem URL HTTP (Storage),
 * baixa e converte; se ainda for data URL legado, passa direto.
 */
export const ensureFluxoImageDataUrl = async (source: string): Promise<string> => {
  if (isDataUrl(source)) return source;
  if (!isHttpUrl(source)) throw new Error("URL de imagem inválida");

  const response = await fetch(source);
  if (!response.ok) {
    throw new Error(`Não foi possível baixar a imagem do fluxo (${response.status})`);
  }

  const blob = await response.blob();
  return blobToDataUrl(blob);
};

/**
 * send-audio exige URL pública. Data URLs legados sobem no chat-audios;
 * URLs do fluxo-media (ou qualquer http) passam direto.
 */
export const resolveFluxoAudioUrl = async (
  source: string,
  fileName?: string
): Promise<string> => {
  if (!isDataUrl(source)) return source;
  if (!supabase) throw new Error("Supabase não configurado");

  const blob = dataUrlToBlob(source);
  const extension = resolveExtension("audio", blob.type, fileName);
  const filePath = `fluxos/audio_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${extension}`;

  const { error: uploadError } = await supabase.storage
    .from(LEGACY_AUDIO_BUCKET)
    .upload(filePath, blob, {
      contentType: blob.type || "audio/mpeg",
      cacheControl: "3600",
      upsert: false,
    });

  if (uploadError) {
    throw new Error(`Não foi possível enviar o áudio do fluxo: ${uploadError.message}`);
  }

  const { data } = supabase.storage.from(LEGACY_AUDIO_BUCKET).getPublicUrl(filePath);
  if (!data?.publicUrl) {
    throw new Error("Não foi possível gerar o link público do áudio do fluxo");
  }

  return data.publicUrl;
};
